import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { API_URL } from '../../../config/api';
import './BusinessIntelligence.css';

const BusinessIntelligence = () => {
  const { t } = useTranslation();
  const [filter, setFilter] = useState('last30days');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, [filter, startDate, endDate]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter && filter !== 'all') {
        params.timeFilter = filter === 'custom' ? undefined : filter;
        if (filter === 'custom') {
          params.dateFrom = startDate;
          params.dateTo = endDate;
        }
      }
      const response = await axios.get(`${API_URL}/delivery-driver/business-intelligence`, { params });
      setData(response.data);
    } catch (error) {
      console.error('Error fetching driver business intelligence:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (newFilter) => {
    setFilter(newFilter);
    if (newFilter !== 'custom') {
      setStartDate('');
      setEndDate('');
    }
  };

  const formatCurrency = (amount) => `€${(amount || 0).toFixed(2)}`;

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="driver-business-intelligence">
      <div className="bi-header">
        <h2>{t('admin.businessIntelligence')}</h2>
      </div>

      <div className="bi-filters">
        <div className="filter-group">
          <label>{t('admin.timePeriod')}</label>
          <div className="time-filters">
            <button
              className={filter === 'all' ? 'active' : ''}
              onClick={() => handleFilterChange('all')}
            >
              {t('admin.all')}
            </button>
            <button
              className={filter === 'today' ? 'active' : ''}
              onClick={() => handleFilterChange('today')}
            >
              {t('admin.today')}
            </button>
            <button
              className={filter === 'yesterday' ? 'active' : ''}
              onClick={() => handleFilterChange('yesterday')}
            >
              {t('admin.yesterday')}
            </button>
            <button
              className={filter === 'last7days' ? 'active' : ''}
              onClick={() => handleFilterChange('last7days')}
            >
              {t('admin.last7Days')}
            </button>
            <button
              className={filter === 'last30days' ? 'active' : ''}
              onClick={() => handleFilterChange('last30days')}
            >
              {t('admin.last30Days')}
            </button>
            <button
              className={filter === 'custom' ? 'active' : ''}
              onClick={() => handleFilterChange('custom')}
            >
              {t('admin.customRange')}
            </button>
          </div>
        </div>
        {filter === 'custom' && (
          <div className="filter-group">
            <label>{t('admin.dateRange')}</label>
            <div className="date-range">
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
              <span>{t('admin.to')}</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                min={startDate}
              />
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="loading-spinner">{t('admin.loadingMetrics')}</div>
      )}

      {!loading && data && (
        <>
          <div className="driver-metrics-section">
            <h3>{t('admin.deliveryStatistics')}</h3>
            <div className="business-metrics-grid">
              {data.dateRange && (
                <div className="metric-card">
                  <h4>{t('admin.dateRange')}</h4>
                  <p className="metric-value small">
                    {formatDate(data.dateRange.start)} – {formatDate(data.dateRange.end)}
                  </p>
                </div>
              )}
              <div className="metric-card highlight">
                <h4>{t('admin.totalDeliveries')}</h4>
                <p className="metric-value">{data.totalDeliveries}</p>
              </div>
              <div className="metric-card highlight">
                <h4>{t('admin.totalEarnings')}</h4>
                <p className="metric-value">{formatCurrency(data.totalEarnings)}</p>
                <span className="metric-note">{t('admin.deliveryFeesEarned')}</span>
              </div>
              {data.averageRating != null && (
                <div className="metric-card">
                  <h4>{t('admin.customerSatisfaction')}</h4>
                  <p className="metric-value">
                    {data.averageRating.toFixed(1)} / 5.0
                  </p>
                  <span className="metric-note">
                    {data.totalReviews} {t('admin.reviews')}
                  </span>
                </div>
              )}
            </div>
          </div>

          <div className="recent-deliveries-section">
            <h3>{t('admin.recentDeliveries')}</h3>
            {data.recentDeliveries && data.recentDeliveries.length > 0 ? (
              <div className="table-container">
                <table className="driver-bi-table">
                  <thead>
                    <tr>
                      <th>{t('admin.orderNumber')}</th>
                      <th>{t('admin.restaurant')}/{t('admin.store')}</th>
                      <th>{t('admin.deliveryFee')}</th>
                      <th>{t('admin.orderTotal')}</th>
                      <th>{t('admin.deliveredAt')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.recentDeliveries.map((row) => (
                      <tr key={row._id}>
                        <td>{row.orderNumber || row._id?.slice(-6)}</td>
                        <td>{row.restaurantName || '—'}</td>
                        <td>{formatCurrency(row.deliveryFee)}</td>
                        <td>{formatCurrency(row.total)}</td>
                        <td>{formatDateTime(row.deliveredAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="no-data">{t('admin.noDeliveriesInPeriod')}</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default BusinessIntelligence;
